/**
 * Boot loader handoff (27 Sep 2026). The inline loader in index.html is the
 * reputifly.com/portfolio brand intro (star draws, slides, wordmark writes on).
 * It stays up until the first real view has painted — the sign-in screen, or the
 * dashboard with its chat list and the restored chat — instead of fading as soon
 * as auth resolves and leaving an empty dashboard on screen.
 *
 * Mirrors the portfolio's rule: the first load of a browser session also waits
 * for the intro to finish (index.html's __rfBoot.introReady); later loads in the
 * same session leave as soon as the view is ready. A hard cap means the loader can
 * never hold the app hostage.
 */
const HARD_CAP_MS = 9_000;
let released = false;

type Boot = { start?: number; introReady?: Promise<void> };

/** The dashboard's first view is on screen (chat list + restored chat hydrated). */
export function markFirstViewReady(): void {
  (window as any).__rfFirstView = true;
  window.dispatchEvent(new Event('rf:first-view'));
}

export function firstViewReady(): boolean {
  return (window as any).__rfFirstView === true;
}

export function releaseBootLoader(): void {
  if (released) return;
  released = true;
  const boot: Boot = (window as any).__rfBoot ?? {};
  const elapsed = performance.now() - (boot.start ?? 0);
  const cap = new Promise<void>((resolve) => window.setTimeout(resolve, Math.max(0, HARD_CAP_MS - elapsed)));
  void Promise.race([boot.introReady ?? Promise.resolve(), cap]).then(() => {
    const el = document.getElementById('boot-loader');
    if (!el) return;
    // A background tab gets no animation frames: drop the loader straight away so the
    // app is simply there when the tab is opened.
    if (document.hidden) { el.remove(); return; }
    let faded = false;
    const fade = () => {
      if (faded) return;
      faded = true;
      el.classList.add('is-fading');
      window.setTimeout(() => el.remove(), 420);
    };
    // Two frames so the view underneath has actually painted before the fade; the timer
    // covers a browser that throttles frames.
    requestAnimationFrame(() => requestAnimationFrame(fade));
    window.setTimeout(fade, 150);
  });
}
